import { inject } from '@angular/core'
import { CanActivateFn, UrlTree } from '@angular/router'

import { Observable, first, map, of, switchMap } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { environment } from '@flowx/designer/environment'
import { EnumerationService } from '@flowx/designer/modules/ui-designer/services/enumerations.service'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { DefaultPermissionsGuard } from '@flowx/designer/shared/guards/workspaces-permissions.guards'

import type { FlxThemePageComponent } from './theme-page.component'

/**
 * Asks before leaving the theme page with unsaved changes, after any running save has finished (its
 * success clears what it sent). Registered on the page's own route.
 */
export const themeCanDeactivateGuard = (
  page: FlxThemePageComponent | null
): Observable<boolean> => {
  const modalService = inject(FlxModalService)
  const showConfirmModal$ = (): Observable<boolean> =>
    modalService
      .show(
        ConfirmationModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          title: `Changes Unsaved`,
          message: `You have unsaved changes. Are you sure you want to exit page?`,
          btnOkText: `Discard Changes`,
          modalClass: 'pending-changes',
        }
      )
      .data$.pipe(
        first(),
        map(({ confirm }) => confirm)
      )
  const confirmIfUnsaved = (): Observable<boolean> =>
    page?.hasUnsavedChanges() ? showConfirmModal$() : of(true)
  return page?.isSaving() ? page.whenSaved().pipe(switchMap(confirmIfUnsaved)) : confirmIfUnsaved()
}

export function themeCanActivateGuard(): CanActivateFn {
  const canReadThemes = DefaultPermissionsGuard('theme_read')

  return (): Observable<boolean | UrlTree> => {
    // TODO Theming: Check where this call should be triggered, in a resolver in
    inject(EnumerationService)
      .getGlobalStaticAssets(environment.baseUrl, environment.staticAssetsPath)
      .subscribe()

    return canReadThemes()
  }
}
